import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Search, Truck, Clock } from 'lucide-react';
import { formatCurrency, SortableHeader, sortAndFilter } from './helpers';

export const EquipmentTab = ({ equipment, pending }) => {
  const [sort, setSort] = useState({ field: 'date', dir: 'desc' });
  const [filter, setFilter] = useState('');

  const sortedEntries = sortAndFilter(equipment?.entries, sort, filter, ['equipmentName', 'date']);
  const pendingEquipmentItems = pending?.entries?.filter(e => e.type === 'EQUIPMENT') || [];

  return (
    <Card className='border-slate-200 shadow-sm rounded-sm'>
      <CardHeader>
        <div className='flex items-center justify-between flex-wrap gap-4'>
          <CardTitle className='text-xl font-bold text-slate-800'>
            Internal Equipment Usage
            <span className='ml-2 text-lg font-normal text-amber-600'>Total: {formatCurrency(equipment?.total || 0)}</span>
          </CardTitle>
          <div className='relative'>
            <Search className='absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400' />
            <Input placeholder='Search equipment...' value={filter} onChange={(e) => setFilter(e.target.value)}
              className='pl-9 w-48 rounded-sm' data-testid="equipment-search" />
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {pendingEquipmentItems.length > 0 && (
          <div className='mb-4 p-3 bg-amber-50 border border-amber-200 rounded-sm'>
            <p className='text-sm font-bold text-amber-800 mb-2'>
              <Clock className='w-4 h-4 inline mr-1' />Pending Equipment Items ({pendingEquipmentItems.length})
            </p>
            <div className='space-y-1'>
              {pendingEquipmentItems.map(entry => (
                <div key={entry.id} className='flex justify-between text-sm'>
                  <span className='text-amber-700'>
                    <Badge variant='outline' className='text-xs mr-2 bg-amber-100'>PENDING</Badge>
                    {entry.vendor} - {entry.poNumber}
                  </span>
                  <span className='font-mono text-amber-800'>{formatCurrency(entry.totalWithMarkup)}</span>
                </div>
              ))}
            </div>
          </div>
        )}
        {(!equipment?.entries || equipment.entries.length === 0) && pendingEquipmentItems.length === 0 ? (
          <div className='text-center py-8'>
            <Truck className='w-12 h-12 mx-auto text-slate-400 mb-4' />
            <p className='text-slate-500'>No internal equipment charges for this job</p>
          </div>
        ) : (
          <div className='overflow-x-auto'>
            <table className='w-full'>
              <thead className='bg-slate-50'>
                <tr>
                  <SortableHeader field='date' label='Date' sortState={sort} setSortState={setSort} />
                  <SortableHeader field='equipmentName' label='Equipment' sortState={sort} setSortState={setSort} />
                  <SortableHeader field='hours' label='Hours' sortState={sort} setSortState={setSort} align='right' />
                  <th className='text-right p-3 text-xs font-bold uppercase tracking-wider text-slate-600'>Rate</th>
                  <SortableHeader field='totalCharge' label='Total' sortState={sort} setSortState={setSort} align='right' />
                  <th className='text-left p-3 text-xs font-bold uppercase tracking-wider text-slate-600'>Notes</th>
                </tr>
              </thead>
              <tbody>
                {sortedEntries.map((entry, index) => (
                  <tr key={entry.id || index} className={index % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                    <td className='p-3 text-slate-700'>{entry.date}</td>
                    <td className='p-3 font-medium text-slate-900'>{entry.equipmentName}</td>
                    <td className='p-3 text-right font-mono'>{entry.hours.toFixed(1)}</td>
                    <td className='p-3 text-right font-mono text-slate-600'>${entry.hourlyRate.toFixed(2)}/hr</td>
                    <td className='p-3 text-right font-mono font-bold text-amber-600'>{formatCurrency(entry.totalCharge)}</td>
                    <td className='p-3 text-sm text-slate-600'>{entry.notes || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
